/**
 * Who may take an account's statement out of Timely (owner decision,
 * 2026-10-17): the printable statement and its spreadsheet are both exports —
 * a complete statement of what a customer owes, as a file that can be saved,
 * sent and shared — so both need **Export reports** (`reports.export`), as
 * every export in the product does. Being able to open the account is not
 * enough: a collector reads the bills on the account's page and does not take
 * them out of the building.
 *
 * The statement is drawn in the browser from what the account's page already
 * reads (`customers_read`, `source_account_detail`, which the database limits
 * to the person's own company and accounts), so the boundary is here: the
 * printable statement's address refuses before it reads anything, and the
 * spreadsheet is refused by the function that builds it, whatever button
 * asked. Hiding the buttons is a courtesy on top.
 *
 * A narrower right ("Download customer statement") for collectors who send
 * statements without exporting anything else is a possible later change; it
 * is not built.
 */
import { csvFileName, holds, statementCsv, type AccountStatement, type Capability, type TenantContext } from '@timely/domain';
import { downloadCsv } from '../reports/useReports';

export const STATEMENT_EXPORT: Capability = 'reports.export';

export const mayExportStatement = (context: TenantContext): boolean => holds(context, STATEMENT_EXPORT);

/**
 * The statement as a spreadsheet, handed to the browser — or nothing, for
 * someone without the right. Returns whether the file was made.
 */
export function exportStatementCsv(context: TenantContext, statement: AccountStatement, customerName: string): boolean {
    if (!mayExportStatement(context)) return false;
    downloadCsv(csvFileName('statement', customerName, statement.asOf), statementCsv(statement, context.workspace.currency));
    return true;
}
